import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { isAdmin } from '../../utils/permissions';

/**
 * LicenseViewModal
 *
 * Tailwind (light theme) modal dialog that fetches license
 * information on open via Axios GET, authenticated with a
 * JWT bearer token.
 *
 * Usage:
 *   <LicenseViewModal
 *     isOpen={isOpen}
 *     onClose={() => setIsOpen(false)}
 *   />
 */
export default function LicenseViewModal({ isOpen, onClose }) {
  const [license, setLicense] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLicense = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const API_BASE = window.MANTLE_CONFIG.API_URL ?? 'http://localhost:5000/api';
      const token = localStorage.getItem('jwt');
      const response = await axios.get(`${API_BASE}/licenses/view`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-db-name': window.MANTLE_CONFIG.TENANT_DB
        },
      });

      console.log(JSON.stringify(response.data));

      // Support either a raw payload or a { success, data } envelope
      const payload = response.data?.data ?? response.data;
      setLicense(payload);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to load license information!";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchLicense();
    } else {
      // Reset state so the next open starts clean
      setLicense(null);
      setError(null);
    }
  }, [isOpen, fetchLicense]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="license-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl ring-1 ring-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2
            id="license-modal-title"
            className="text-base font-semibold text-slate-900"
          >
            License Information
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              className="h-5 w-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <svg
                className="h-6 w-6 animate-spin text-slate-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            </div>
          )}

          {!isLoading && error && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-100">
              {error}
            </div>
          )}

          {!isLoading && !error && license && (
            <dl className="space-y-4">
              <Row label="License Code" value={license.lcode} mono />
              <Row label="Expiry Date" value={formatDate(license.expiry_date)} />
              <Row label="Maximum Site Count" value={license.max_sites} />

              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Modules
                </dt>
                <dd className="mt-2 flex flex-wrap gap-1.5">
                  {(license.modules ?? []).length > 0 ? (
                    license.modules.map((mod) => (
                      <span
                        key={mod}
                        className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                      >
                        {mod}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-slate-500">None</span>
                  )}
                </dd>
              </div>
            </dl>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          {!isLoading && error && (
            <button
              type="button"
              onClick={fetchLicense}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Retry
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd
        className={`text-sm text-slate-900 ${mono ? "font-mono" : "font-medium"}`}
      >
        {value ?? "—"}
      </dd>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-ZA', {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }) + " (" + calculateDaysFromToday(value) + " days)";
}

function calculateDaysFromToday(endDate) {
  const currentDate = new Date();
  const end = new Date(endDate);
  const timeDifference = end - currentDate;
  let daysDiff = Math.ceil(timeDifference / (1000 * 3600 * 24)); // Convert milliseconds to days and use Math.ceil to avoid fractional days

  return daysDiff;
}
